import { SimpleGrid, HStack, Text, Icon, Center, Spinner } from '@chakra-ui/react';

import SectionContainer from '~/containers/SectionContainer';
import EmptySection from '~/components/EmptySection';

import { PopularMealList, PopularRestaurantList } from './components';

import pickMealAtAtom from '~/recoil/atom/pickMealAt';
import { useRecoilValue } from 'recoil';

import { IoRestaurantOutline, IoArrowForward } from 'react-icons/io5';

import { AiOutlineShop } from 'react-icons/ai';

import fp from 'lodash/fp';

import { FetchingDataStatus } from '~/types/utilities';

import { AlgoliaStore } from '~/types/store/store';
import { ItemWithId } from '~/types/store/item';

type DisplayModeProps = {
  fetchingStatus: FetchingDataStatus;
  filteredStores: Array<AlgoliaStore>;
  composedPopularItems: Array<ItemWithId & { storeId: string }>;
  handleNavigateToStoreList: () => void;
  handleNavigateToItemList: () => void;
};

export default function DisplayMode({
  fetchingStatus,
  filteredStores,
  composedPopularItems,
  handleNavigateToStoreList,
  handleNavigateToItemList,
}: DisplayModeProps) {
  const pickMealAt = useRecoilValue(pickMealAtAtom);

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 12 }}>
      <SectionContainer
        title="熱門餐廳"
        icon={<Icon as={AiOutlineShop} color="secondary" boxSize="1.25rem" />}
        rightHandComponent={
          <HStack align="center" spacing={1} cursor="pointer" onClick={handleNavigateToStoreList}>
            <Text fontSize="xs" color="primary">
              查看更多
            </Text>
            <Icon color="primary" as={IoArrowForward} />
          </HStack>
        }
      >
        {fp.cond([
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.LOADING),
            }),
            () => (
              <Center>
                <Spinner size="lg" />
              </Center>
            ),
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.FAIL),
            }),
            () => <EmptySection content="獲取餐廳發生錯誤，請重試" />,
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              storesLength: fp.eq(0),
            }),
            () => <EmptySection content="目前無餐廳" />,
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              storesLength: fp.lt(0),
            }),
            () => (
              <PopularRestaurantList
                storeList={filteredStores.slice(0, 5)}
                pickMealTime={pickMealAt}
              />
            ),
          ],
          [fp.T, fp.always(null)],
        ])({ state: fetchingStatus, storesLength: filteredStores.length })}
      </SectionContainer>
      <SectionContainer
        title="熱門餐點"
        icon={<Icon as={IoRestaurantOutline} color="secondary" boxSize="1.25rem" />}
        rightHandComponent={
          <HStack align="center" spacing={1} cursor="pointer" onClick={handleNavigateToItemList}>
            <Text fontSize="xs" color="primary">
              查看更多
            </Text>
            <Icon color="primary" as={IoArrowForward} />
          </HStack>
        }
      >
        {fp.cond([
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.LOADING),
            }),
            () => (
              <Center>
                <Spinner size="lg" />
              </Center>
            ),
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.FAIL),
            }),
            () => <EmptySection content="獲取資料發生錯誤請重試" />,
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              itemsLength: fp.eq(0),
            }),
            () => <EmptySection content="目前無開放點餐餐點" />,
          ],
          [
            fp.where({
              state: fp.eq(FetchingDataStatus.SUCCESS),
              itemsLength: fp.lt(0),
            }),
            () => <PopularMealList items={composedPopularItems.slice(0, 5)} />,
          ],
          [fp.T, fp.always(null)],
        ])({ state: fetchingStatus, itemsLength: composedPopularItems.length })}
      </SectionContainer>
    </SimpleGrid>
  );
}
